import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction, AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Edit, Trash2, User as UserIcon, Building2, Phone, Mail, MapPin,
  Car, Plus, Calendar, FileText, Wrench, ShieldCheck, Activity, Clock, AlertCircle,
  Copy, Check,
  Sparkles, Loader2, Star, Link2, Bot,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ClientSuiviServicesCard from "@/components/ClientSuiviServicesCard";
import { useAuth } from "@/contexts/AuthContext";
import HistoriqueLinks, { HistoriqueDescription } from "@/components/HistoriqueLinks";
import GardiennageBlock from "@/components/GardiennageBlock";
import SuiviShareDialog from "@/components/SuiviShareDialog";

const STATUT_BADGE = {
  actif: "bg-green-50 text-green-700 border-green-200",
  inactif: "bg-gray-100 text-gray-700 border-gray-200",
  bloque: "bg-red-50 text-red-700 border-red-200",
};

const ABO_BADGE = {
  S: "bg-blue-50 text-blue-700 border-blue-200",
  M: "bg-purple-50 text-purple-700 border-purple-200",
  L: "bg-amber-50 text-amber-700 border-amber-200",
  flotte: "bg-[#52a675]/10 text-[#3d8a5c] border-[#52a675]/30",
  aucun: "bg-gray-50 text-gray-500 border-gray-200",
};

const TYPE_ICON = {
  rdv: Clock, intervention: Wrench, pret: Car, facture: FileText,
  paiement: ShieldCheck, appel: Phone, note: FileText, systeme: Activity,
};

const EMPTY_VEH = {
  plaque: "", vin: "", marque: "", modele: "", premiere_mec: "",
  carburant: "essence", km_dernier: "",
  type_entretien: "standard", est_principal: false,
};

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [rdvAutonomes, setRdvAutonomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [vehDialogOpen, setVehDialogOpen] = useState(false);
  const [editingVeh, setEditingVeh] = useState(null);
  const [vehForm, setVehForm] = useState(EMPTY_VEH);
  // iter55 — feedback copie VIN par véhicule
  const [vinCopied, setVinCopied] = useState(null);
  // iter163.5 — Recherche véhicules par plaque/marque/modèle (utile pour Guido & flottes)
  const [vehSearch, setVehSearch] = useState("");
  // iter234 — Onglet contrôlé + arrivée depuis l'historique (?vehicule=<id>)
  const [searchParams] = useSearchParams();
  const vehiculeParam = searchParams.get("vehicule");
  const [tab, setTab] = useState(vehiculeParam ? "vehicules" : "vue");
  const [shareOpen, setShareOpen] = useState(false);   // iter238 — lien de suivi public
  const copyVin = async (vid, vin) => {
    try {
      await navigator.clipboard.writeText(vin);
      setVinCopied(vid);
      setTimeout(() => setVinCopied(null), 1500);
    } catch {
      /* clipboard non disponible */
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, h, ra] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/historique`),
        api.get(`/clients/${id}/rdv-autonomes`).catch(() => ({ data: { items: [] } })),
      ]);
      setClient(c.data);
      setHistorique(h.data);
      setRdvAutonomes(ra.data.items || []);
    } catch (e) {
      toast.error("Client introuvable");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  // iter234 — Arrivée depuis l'historique sur un véhicule précis : onglet + scroll
  useEffect(() => {
    if (!vehiculeParam || !client) return;
    setTab("vehicules");
    const t = setTimeout(() => {
      document.getElementById(`veh-card-${vehiculeParam}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(t);
  }, [vehiculeParam, client]);

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/clients/${id}/notes`, { description: note.trim() });
      toast.success("Note ajoutée");
      setNote("");
      const { data } = await api.get(`/clients/${id}/historique`);
      setHistorique(data);
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Client supprimé");
      navigate("/clients");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openVehDialog = (veh = null) => {
    if (veh) {
      setEditingVeh(veh);
      setVehForm({ ...EMPTY_VEH, ...veh });
    } else {
      setEditingVeh(null);
      setVehForm({ ...EMPTY_VEH, est_principal: client.vehicules.length === 0 });
    }
    setVehDialogOpen(true);
  };

  const saveVehicule = async () => {
    try {
      const payload = Object.fromEntries(
        Object.entries(vehForm).map(([k, v]) => [k, v === "" ? null : v])
      );
      // iter66 — Dérive annee depuis premiere_mec si fournie (rétrocompat affichage)
      if (payload.premiere_mec && payload.premiere_mec.length >= 4) {
        payload.annee = parseInt(payload.premiere_mec.slice(0, 4), 10);
      }
      if (payload.km_dernier) payload.km_dernier = parseInt(payload.km_dernier, 10);
      if (!payload.plaque || !payload.marque || !payload.modele) {
        toast.error("Plaque, marque et modèle requis");
        return;
      }
      // iter66 — Pré-check unicité plaque
      try {
        const params = { plaque: payload.plaque };
        if (editingVeh?.id) params.exclude_id = editingVeh.id;
        const { data: chk } = await api.get("/vehicules/check-plaque", { params });
        if (!chk.available && chk.conflict) {
          const c = chk.conflict;
          toast.error(`Plaque déjà attribuée à ${c.marque || ""} ${c.modele || ""} de ${c.client_nom}`);
          return;
        }
      } catch (e) { /* silent — backend revérifiera */ }
      if (editingVeh) {
        await api.put(`/vehicules/${editingVeh.id}`, payload);
        toast.success("Véhicule mis à jour");
      } else {
        await api.post(`/clients/${id}/vehicules`, payload);
        toast.success("Véhicule ajouté");
      }
      setVehDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const removeVehicule = async (vid) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    try {
      await api.delete(`/vehicules/${vid}`);
      toast.success("Véhicule supprimé");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (loading || !client) return (
    <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
  );

  const name = client.raison_sociale || `${client.prenom || ""} ${client.nom || ""}`.trim();
  const Icon = client.type === "assurance" ? ShieldCheck : (client.type === "entreprise" ? Building2 : UserIcon);
  const typeLabel = client.type === "assurance" ? "Assurance" : (client.type === "entreprise" ? "Entreprise" : "Particulier");
  const principal = client.vehicules.find((v) => v.est_principal) || client.vehicules[0];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="client-detail-page">
      <button
        onClick={() => navigate("/clients")}
        className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3"
        data-testid="back-to-list"
      >
        <ArrowLeft className="w-4 h-4" /> Retour à la liste
      </button>

      {/* Header */}
      <Card className="p-6 border-gray-200 mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className={`w-14 h-14 rounded-lg ${client.type === "assurance" ? "bg-violet-50 text-violet-600" : (client.type === "entreprise" ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600")} flex items-center justify-center`}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-2xl font-heading font-bold text-gray-900" data-testid="client-name">{name}</h1>
              <Badge variant="outline" className={STATUT_BADGE[client.statut]}>{client.statut}</Badge>
              {client.abonnement && client.abonnement !== "aucun" && (
                <Badge variant="outline" className={ABO_BADGE[client.abonnement]}>
                  <Sparkles className="w-3 h-3 mr-1" />
                  CarMaintenance {client.abonnement.toUpperCase()}
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-500">
              {typeLabel}
              {client.ide && ` · IDE ${client.ide}`}
              {client.num_bexio && ` · Bexio #${client.num_bexio}`}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
              <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" />{client.tel_principal}</span>
              {client.email && <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-gray-400" />{client.email}</span>}
              <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-gray-400" />{client.adresse}, {client.npa} {client.localite}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="client-share-suivi-btn">
              <Link2 className="w-4 h-4 mr-2" /> Partager le suivi
            </Button>
            <Button variant="outline" onClick={() => navigate(`/clients/${id}/modifier`)} data-testid="edit-client-button">
              <Edit className="w-4 h-4 mr-2" /> Modifier
            </Button>
            {user?.role === "admin" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="delete-client-button">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est définitive et supprimera également tous les véhicules et l'historique associés.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-client">
                      Supprimer définitivement
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full overflow-x-auto justify-start no-scrollbar flex-nowrap">
          <TabsTrigger value="vue" data-testid="tab-vue">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="vehicules" data-testid="tab-vehicules">Véhicules ({client.vehicules.length})</TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-historique">Historique ({historique.length})</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        {/* Vue d'ensemble */}
        <TabsContent value="vue" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5 border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-gray-900">Véhicule principal</h2>
                {client.vehicules.length === 0 && (
                  <Button size="sm" variant="outline" onClick={() => openVehDialog()} data-testid="add-first-vehicule">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                  </Button>
                )}
              </div>
              {principal ? (
                <div>
                  <div className="text-lg font-bold text-gray-900">{principal.plaque}</div>
                  <div className="text-sm text-gray-600">{principal.marque} {principal.modele} {principal.premiere_mec && `· ${principal.premiere_mec.slice(0, 4)}`}</div>
                  {principal.km_dernier && <div className="text-xs text-gray-500 mt-1">{principal.km_dernier.toLocaleString()} km</div>}
                </div>
              ) : (
                <div className="text-sm text-gray-400">Aucun véhicule enregistré</div>
              )}
            </Card>

            <Card className="p-5 border-gray-200">
              <h2 className="font-heading font-semibold text-gray-900 mb-3">En cours</h2>
              <div className="text-sm text-gray-400">
                <div className="flex items-center gap-2 py-1.5"><Calendar className="w-4 h-4" /> Aucun RDV à venir (Module Calendrier · Phase 2)</div>
                <div className="flex items-center gap-2 py-1.5"><Wrench className="w-4 h-4" /> Aucune intervention en cours (Module Atelier · Phase 2)</div>
                <div className="flex items-center gap-2 py-1.5"><FileText className="w-4 h-4" /> Aucune facture impayée (Module Facturation · Vague 2)</div>
              </div>
            </Card>
          </div>

          {/* iter245 — Services en ligne (suivi + messagerie) */}
          <div className="mt-4">
            <ClientSuiviServicesCard
              client={client}
              onUpdated={(c) => setClient((prev) => ({ ...prev, ...c }))}
            />
          </div>

          {/* iter235 — Gardiennage pneus du client (toutes plaques) */}
          <GardiennageBlock clientId={id} />

          {client.notes && (            <Card className="p-5 mt-4 border-gray-200">
              <h2 className="font-heading font-semibold text-gray-900 mb-2">Notes</h2>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{client.notes}</p>
            </Card>
          )}
        </TabsContent>

        {/* Véhicules */}
        <TabsContent value="vehicules" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            {client.vehicules.length > 3 ? (
              <div className="relative flex-1 max-w-md">
                <Input
                  value={vehSearch}
                  onChange={(e) => setVehSearch(e.target.value)}
                  placeholder={`Rechercher parmi ${client.vehicules.length} véhicules (plaque, marque, modèle, VIN)…`}
                  className="pl-9"
                  data-testid="veh-search-input"
                />
                <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
            ) : <div />}
            <Button onClick={() => openVehDialog()} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="add-vehicule-button">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un véhicule
            </Button>
          </div>
          {(() => {
            const q = vehSearch.trim().toLowerCase();
            const filteredVehs = q
              ? client.vehicules.filter((v) =>
                  (v.plaque || "").toLowerCase().includes(q) ||
                  (v.marque || "").toLowerCase().includes(q) ||
                  (v.modele || "").toLowerCase().includes(q) ||
                  (v.vin || "").toLowerCase().includes(q))
              : client.vehicules;
            return (
              <>
                {q && (
                  <div className="text-xs text-gray-500 mb-2" data-testid="veh-search-count">
                    {filteredVehs.length} véhicule{filteredVehs.length > 1 ? "s" : ""} trouvé{filteredVehs.length > 1 ? "s" : ""}
                    {filteredVehs.length !== client.vehicules.length && ` sur ${client.vehicules.length}`}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-4">
                  {client.vehicules.length === 0 && (
                    <div className="md:col-span-2 text-center py-12 text-gray-400 bg-white rounded-md border border-dashed border-gray-200">
                      Aucun véhicule enregistré pour ce client
                    </div>
                  )}
                  {q && filteredVehs.length === 0 && client.vehicules.length > 0 && (
                    <div className="md:col-span-2 text-center py-12 text-gray-400 bg-white rounded-md border border-dashed border-gray-200" data-testid="veh-search-empty">
                      Aucun véhicule ne correspond à «&nbsp;{vehSearch}&nbsp;»
                    </div>
                  )}
                  {filteredVehs.map((v) => (
              <Card
                key={v.id}
                id={`veh-card-${v.id}`}
                className={`p-4 border-gray-200 ${vehiculeParam === v.id ? "ring-2 ring-[#52a675] border-[#52a675]" : ""}`}
                data-testid={`vehicule-card-${v.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="w-11 h-11 rounded-md bg-gray-100 flex items-center justify-center">
                    <Car className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-gray-900">{v.plaque}</div>
                      {v.est_principal && (
                        <Badge variant="outline" className="bg-[#52a675]/10 text-[#3d8a5c] border-[#52a675]/30 text-[10px]">
                          <Star className="w-3 h-3 mr-0.5" /> Principal
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-700">{v.marque} {v.modele}{v.premiere_mec && ` · ${v.premiere_mec.slice(0, 4)}`}</div>
                    {/* iter55 — VIN visible et copiable inline */}
                    {v.vin && (
                      <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                        <span className="font-mono">VIN: {v.vin}</span>
                        <button
                          type="button"
                          onClick={() => copyVin(v.id, v.vin)}
                          className="text-gray-400 hover:text-emerald-600 transition"
                          title="Copier le VIN"
                          data-testid={`copy-vin-${v.id}`}
                        >
                          {vinCopied === v.id ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                        </button>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-0.5">
                      {v.carburant && <span className="capitalize">{v.carburant}</span>}
                      {v.km_dernier && ` · ${v.km_dernier.toLocaleString()} km`}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openVehDialog(v)} data-testid={`edit-vehicule-${v.id}`}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => removeVehicule(v.id)} data-testid={`delete-vehicule-${v.id}`} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
              </>
            );
          })()}
        </TabsContent>

        {/* Historique */}
        <TabsContent value="historique" className="mt-4">
          {/* iter370 — Ce que le client a pris lui-même, avec Léa ou en ligne */}
          {rdvAutonomes.length > 0 && (
            <Card className="p-5 border-gray-200 mb-4" data-testid="client-rdv-autonomes">
              <div className="flex items-center gap-2 mb-3">
                <Bot className="w-4 h-4 text-[#52a675]" />
                <h2 className="font-heading font-semibold text-gray-900">
                  Rendez-vous pris par le client ({rdvAutonomes.length})
                </h2>
              </div>
              <div className="space-y-1.5">
                {rdvAutonomes.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 bg-gray-50/60"
                    data-testid={`client-rdv-autonome-${r.id}`}
                  >
                    <span className="text-xs font-semibold text-gray-700 w-28 flex-shrink-0">
                      {new Date(`${r.date}T12:00:00`).toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "2-digit" })}
                      {r.heure_debut ? ` · ${r.heure_debut}` : ""}
                    </span>
                    <span className="text-sm text-gray-700 flex-1 truncate">
                      {[r.vehicule_label, r.type_intervention_nom].filter(Boolean).join(" · ")}
                    </span>
                    {r.or_numero && <span className="font-mono text-[10px] text-gray-400">{r.or_numero}</span>}
                    <Badge variant="outline" className="text-[10px] bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/30">
                      {r.canal}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Card className="p-5 border-gray-200">
            {historique.length === 0 ? (
              <div className="text-center py-8 text-gray-400">Aucun événement dans l'historique</div>
            ) : (
              <ol className="relative border-l-2 border-gray-100 ml-3 space-y-4">
                {historique.map((h) => {
                  const IconC = TYPE_ICON[h.type] || Activity;
                  return (
                    <li key={h.id} className="ml-6 relative">
                      <span className="absolute -left-9 top-0 w-6 h-6 rounded-full bg-white border-2 border-[#52a675]/30 flex items-center justify-center">
                        <IconC className="w-3 h-3 text-[#52a675]" />
                      </span>
                      <HistoriqueDescription entry={h} />
                      <HistoriqueLinks entry={h} />
                      <div className="text-xs text-gray-500 mt-0.5">
                        {new Date(h.date).toLocaleString("fr-CH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {h.user_nom && ` · ${h.user_nom}`}
                        {h.origine === "bexio" && " · importé de Bexio"}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </Card>
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" className="mt-4">
          <Card className="p-5 border-gray-200">
            <Label className="mb-2 block">Ajouter une note</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Note interne (visible par toute l'équipe)…"
              data-testid="new-note-textarea"
            />
            <div className="mt-3 flex justify-end">
              <Button onClick={addNote} disabled={!note.trim()} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="save-note-button">
                Ajouter la note
              </Button>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="font-semibold text-sm text-gray-900 mb-3">Notes précédentes</h3>
              <div className="space-y-2">
                {historique.filter((h) => h.type === "note").length === 0 && (
                  <div className="text-sm text-gray-400">Aucune note</div>
                )}
                {historique.filter((h) => h.type === "note").map((n) => (
                  <div key={n.id} className="p-3 bg-yellow-50 border border-yellow-100 rounded-md">
                    <div className="text-sm text-gray-900 whitespace-pre-wrap">{n.description}</div>
                    <div className="text-[11px] text-gray-500 mt-1">
                      {new Date(n.date).toLocaleString("fr-CH")} · {n.user_nom}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog véhicule */}
      <Dialog open={vehDialogOpen} onOpenChange={setVehDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingVeh ? "Modifier le véhicule" : "Ajouter un véhicule"}</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Plaque *</Label>
              <Input value={vehForm.plaque} onChange={(e) => setVehForm({ ...vehForm, plaque: e.target.value })} data-testid="dialog-veh-plaque" />
            </div>
            <div>
              <Label>Marque *</Label>
              <Input value={vehForm.marque} onChange={(e) => setVehForm({ ...vehForm, marque: e.target.value })} data-testid="dialog-veh-marque" />
            </div>
            <div>
              <Label>Modèle *</Label>
              <Input value={vehForm.modele} onChange={(e) => setVehForm({ ...vehForm, modele: e.target.value })} data-testid="dialog-veh-modele" />
            </div>
            <div>
              <Label>1ère mise en circulation</Label>
              <Input type="date" value={vehForm.premiere_mec || ""} onChange={(e) => setVehForm({ ...vehForm, premiere_mec: e.target.value })} data-testid="dialog-veh-premiere-mec" />
            </div>
            <div>
              <Label>Carburant</Label>
              <Select value={vehForm.carburant || "essence"} onValueChange={(v) => setVehForm({ ...vehForm, carburant: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="essence">Essence</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="hybride">Hybride</SelectItem>
                  <SelectItem value="electrique">Électrique</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kilométrage</Label>
              <Input type="number" value={vehForm.km_dernier || ""} onChange={(e) => setVehForm({ ...vehForm, km_dernier: e.target.value })} />
            </div>
            <div>
              <Label>VIN</Label>
              <Input value={vehForm.vin || ""} onChange={(e) => setVehForm({ ...vehForm, vin: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 17) })} maxLength={17} />
            </div>
            <div>
              <Label>Type d'entretien</Label>
              <Select value={vehForm.type_entretien || "standard"} onValueChange={(v) => setVehForm({ ...vehForm, type_entretien: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="abonnement_s">Abonnement S</SelectItem>
                  <SelectItem value="abonnement_m">Abonnement M</SelectItem>
                  <SelectItem value="abonnement_l">Abonnement L</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!vehForm.est_principal}
                onChange={(e) => setVehForm({ ...vehForm, est_principal: e.target.checked })}
                id="est-principal"
                data-testid="dialog-veh-principal"
              />
              <Label htmlFor="est-principal" className="cursor-pointer">Véhicule principal du client</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVehDialogOpen(false)}>Annuler</Button>
            <Button onClick={saveVehicule} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="dialog-veh-save">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SuiviShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        clientId={id}
        clientLabel={name}
      />
    </div>
  );
}
